import { api, getToken } from "./api";

import { ENDPOINTS } from "@/constants/endpoints";
import { route } from "@/utils/route";
import { IntegrationConnection, UserOnboarding } from "@cohesive/api-client";
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useCallback } from "react";
import { getOrgIdFromToken } from "./utils";

export function useUserOnboarding(
  options?: Partial<UseQueryOptions<UserOnboarding, AxiosError>>,
) {
  return useQuery<UserOnboarding, AxiosError>({
    queryKey: ["userOnboarding"],
    queryFn: async () => {
      const token = await getToken();
      const orgId = getOrgIdFromToken(token);
      const response = await api.get<{ data: UserOnboarding }>(
        route(ENDPOINTS.ORG.ONBOARDING.SHOW, {
          organization_id: orgId,
        }),
      );

      return response.data.data;
    },
    // Session state, not view data: a restored snapshot can resurrect a
    // template prompt that was cleared moments before the reload — the persist
    // flush is throttled and the rehydrated entry can land inside staleTime,
    // so no refetch would correct it.
    meta: { persist: false },
    ...options,
  });
}

export function useUpdateUserOnboarding(
  options?: Partial<
    UseMutationOptions<UserOnboarding, AxiosError, UserOnboarding>
  >,
) {
  return useMutation<UserOnboarding, AxiosError, UserOnboarding>({
    mutationFn: async (onboarding) => {
      const token = await getToken();
      const orgId = getOrgIdFromToken(token);
      const response = await api.patch(
        route(ENDPOINTS.ORG.ONBOARDING.UPDATE, {
          organization_id: orgId,
        }),
        onboarding,
      );

      return response.data.data;
    },
    ...options,
  });
}

/**
 * Resolve the template prompt from a public-template signup. `null` is the
 * API's clear sentinel; omitting the field would leave it untouched. The PATCH
 * replaces the whole object, so it carries the rest of the record verbatim.
 */
export function useClearTemplatePrompt() {
  const queryClient = useQueryClient();
  const { mutate } = useUpdateUserOnboarding({
    onSuccess: (data) => {
      queryClient.setQueryData(["userOnboarding"], data);
    },
  });

  return useCallback(() => {
    const currentData = queryClient.getQueryData<UserOnboarding>([
      "userOnboarding",
    ]);
    if (currentData) {
      mutate({ ...currentData, template_prompt: null });
    }
  }, [mutate, queryClient]);
}

// Function to get the most recent connectable integration connection from a list of connections
export const getMostRecentConnection = (
  connections: IntegrationConnection[],
): IntegrationConnection | null => {
  connections = connections.filter(
    (connection) => connection.integration_provider !== "cohesive",
  );
  if (!connections || connections.length === 0) {
    return null;
  }

  // Sort connections by updated_at date (most recent first)
  const sortedConnections = [...connections].sort((a, b) => {
    return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
  });

  return sortedConnections[0] || null;
};
